/**
 * CSRF Token Management
 * 
 * The backend sets a CSRF token in a readable cookie for authenticated users.
 * For unauthenticated requests (login/register), we need to fetch a public CSRF token.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Cache for public CSRF token
let publicCSRFToken: string | null = null;
let publicCSRFTokenExpiry: number = 0;

/**
 * Get CSRF token from cookie (for authenticated requests)
 */
export function getCSRFToken(): string | null {
  // Parse document.cookie to find csrf_token
  const cookies = document.cookie.split(';');
  
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrf_token') {
      return value;
    }
  }
  
  return null;
}

/**
 * Fetch public CSRF token from backend (for unauthenticated requests)
 */
export async function fetchPublicCSRFToken(): Promise<string | null> {
  // Check if we have a valid cached token
  if (publicCSRFToken && publicCSRFTokenExpiry > Date.now()) {
    return publicCSRFToken;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/csrf-token`, {
      method: 'GET',
      credentials: 'include', // Include cookies if any
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.error('Failed to fetch CSRF token:', response.status);
      return null;
    }

    const data = await response.json();
    if (data.success && data.csrf_token) {
      // Cache the token for 4 minutes (backend uses 5-minute windows)
      publicCSRFToken = data.csrf_token;
      publicCSRFTokenExpiry = Date.now() + 4 * 60 * 1000;
      return data.csrf_token;
    }

    return null;
  } catch (error) {
    console.error('Error fetching CSRF token:', error);
    return null;
  }
}

/**
 * Get CSRF token for any request
 * - For authenticated users: uses cookie-based token
 * - For unauthenticated users: fetches public token
 */
export async function getCSRFTokenForRequest(): Promise<string | null> {
  // First try to get from cookie (authenticated users)
  const cookieToken = getCSRFToken();
  if (cookieToken) {
    return cookieToken;
  }

  // Otherwise fetch public token (login/register)
  return await fetchPublicCSRFToken();
}

/**
 * Add CSRF token to request headers (async version)
 */
export async function addCSRFHeader(headers: Record<string, string>): Promise<Record<string, string>> {
  const csrfToken = await getCSRFTokenForRequest();
  
  if (csrfToken) {
    return {
      ...headers,
      'X-CSRF-Token': csrfToken
    };
  }
  
  return headers;
}

/**
 * Add CSRF token to request headers (sync version for backwards compatibility)
 * @deprecated Use addCSRFHeader instead
 */
export function addCSRFHeaderSync(headers: Record<string, string>): Record<string, string> {
  const csrfToken = getCSRFToken();
  
  if (csrfToken) {
    return {
      ...headers,
      'X-CSRF-Token': csrfToken
    };
  }
  
  return headers;
}

/**
 * Check if a request method requires CSRF protection
 */
export function requiresCSRF(method: string): boolean {
  // Only state-changing methods need CSRF protection
  const protectedMethods = ['POST', 'PUT', 'DELETE', 'PATCH'];
  return protectedMethods.includes(method.toUpperCase());
}